
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useBusinessDiscountHistory } from '@/hooks/useBusinessDiscountHistory';
import { useDiscountSettings } from '@/hooks/useParameters';
import DiscountBadge from '@/components/DiscountBadge';
import CouponCodeButton from '@/components/CouponCodeButton';
import { BusinessDiscount } from '@/hooks/useBusinessDiscount';

interface DiscountHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

const DiscountHistoryModal = ({ isOpen, onClose, businessId }: DiscountHistoryModalProps) => {
  const { data: discounts, isLoading } = useBusinessDiscountHistory(businessId);
  const { shouldDisplayDiscountCode } = useDiscountSettings();

  const getStatusBadge = (status: string, isActive: boolean) => {
    if (isActive && status === 'Published') {
      return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    }
    
    return <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] sm:w-full mx-4 sm:mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Discount History</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {discounts && discounts.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px] w-auto">Discount</TableHead>
                        <TableHead className="min-w-[80px] w-auto">Type</TableHead>
                        <TableHead className="min-w-[100px] w-auto hidden sm:table-cell">Coverage</TableHead>
                        {shouldDisplayDiscountCode && (
                          <TableHead className="min-w-[120px] w-auto hidden md:table-cell">Coupon Code</TableHead>
                        )}
                        <TableHead className="min-w-[80px] w-auto">Status</TableHead>
                        <TableHead className="min-w-[100px] w-auto hidden lg:table-cell">Created</TableHead>
                        <TableHead className="min-w-[100px] w-auto hidden xl:table-cell">Updated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {discounts.map((discount) => (
                        <TableRow key={discount.id}>
                          <TableCell className="min-w-[120px] w-auto">
                            <DiscountBadge
                              amount={discount.discount_amount}
                              isPercentage={discount.is_percentage}
                              appliesToAll={discount.applies_to_all}
                              type={discount.type as 'Paid' | 'Free'}
                              size="sm"
                            />
                          </TableCell>
                          <TableCell className="min-w-[80px] w-auto">
                            <Badge variant="outline" className="text-xs">{discount.type}</Badge>
                          </TableCell>
                          <TableCell className="min-w-[100px] w-auto hidden sm:table-cell">
                            <span className="text-xs">
                              {discount.applies_to_all ? 'All services' : 'Selected'}
                            </span>
                          </TableCell>
                          {shouldDisplayDiscountCode && (
                            <TableCell className="min-w-[120px] w-auto hidden md:table-cell">
                              {discount.type === 'Paid' && discount.coupon_code ? (
                                <CouponCodeButton
                                  couponCode={discount.coupon_code}
                                  variant="badge"
                                  size="sm"
                                />
                              ) : (
                                <span className="text-muted-foreground text-xs">
                                  {discount.type === 'Free' ? 'N/A' : 'No code'}
                                </span>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="min-w-[80px] w-auto">
                            {getStatusBadge(discount.status, discount.is_active)}
                          </TableCell>
                          <TableCell className="min-w-[100px] w-auto hidden lg:table-cell">
                            <span className="text-xs">
                              {new Date(discount.created_at).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell className="min-w-[100px] w-auto hidden xl:table-cell">
                            <span className="text-xs">
                              {new Date(discount.updated_at).toLocaleDateString()}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No discount history found.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiscountHistoryModal;
